import React, { FC, useCallback, useEffect } from "react";

import { FormDateInput, BusyDayDescription } from "stentor-models";

import dayjs, { Dayjs } from "dayjs";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import FormHelperText from "@mui/material/FormHelperText";
import { DayCalendarSkeleton } from "@mui/x-date-pickers/DayCalendarSkeleton";

import { FormFieldProps } from "../FormDisplay";

import { isBusyDay, parseBusyDays } from "../../utils/busyDays";

import { isDisabledByBusyDays } from "./dateAvailability";

import styles from "./FormFieldDate.module.scss";

export const DEFAULT_BUSY_DAYS: BusyDayDescription = {
    blockWeekends: true,
    blockCurrentDay: true,
    blockNextBusinessDays: 2,
};

export interface FormDateProps extends FormFieldProps {
    /**
     * The date field
     */
    readonly field: FormDateInput;
    /**
     * Busy days as dates comma delimited, for example "YYYY-MM-DD,YYYY-MM-DD"
     */
    readonly busyDays?: string;
    /**
     * Are we loading availability information
     */
    readonly loading: boolean;
}

const loadingMessage = "Getting latest availability...";

export const FormFieldDate: FC<FormDateProps> = (props) => {
    // Only go up to 2 months in the future
    const MAX_DATE = dayjs().add(2, "month");
    const TODAY = dayjs();

    const { field, onFieldChange, errorMessage, busyDays, loading } = props;

    // Cant find a more exact definition for this interface
    // Had to look at the props coming in and make a best guess
    interface CustomDayProps extends PickersDayProps<Dayjs> {
        disabled?: boolean;
        day: Dayjs;
        selected?: boolean;
        disableHighlightToday?: boolean;
        today?: boolean;
        outsideCurrentMonth: boolean;
    }

    /**
     * Displays the date depending on if it is disable or in the past
     */
    const CustomDay = (props: CustomDayProps) => {
        const { day, disabled } = props;

        const isPast = day.isBefore(TODAY, "day");

        const isBeyondMaxDate = day.isAfter(MAX_DATE, "day");

        if (isPast || isBeyondMaxDate) {
            return <PickersDay {...props} className={styles.xapp_date_picker_past} />;
        } else if (disabled) {
            return <PickersDay {...props} />;
        }

        // available
        return <PickersDay {...props} className={styles.xapp_date_picker_available} />;
    };

    const RenderLoading = () => <DayCalendarSkeleton />;

    const [dateTimeValue, setDateTimeValue] = React.useState<Dayjs>();

    const onChange = (newValue: Dayjs) => {
        setDateTimeValue(newValue);
    };

    useEffect(() => {
        onFieldChange(field.name, dateTimeValue);
    }, [dateTimeValue, field.name, onFieldChange]);

    const disabledDays = parseBusyDays(busyDays);

    /**
     * Callback to determine if a date should be disabled used by the calendar
     */
    const shouldDisableDate = useCallback(
        (d: Dayjs): boolean => {
            if (!disabledDays) {
                // See if field has any defaultBusyDays by checking keys
                const hasDefaultBusyDays = Object.keys(field?.defaultBusyDays || {}).length > 0;

                // look for defaultBusyDays on the form or use the DEFAULT_BUSY_DAYS
                const busyDays = hasDefaultBusyDays
                    ? // the extra '|| {}' is to make the subsequent types happy
                      field.defaultBusyDays || {}
                    : DEFAULT_BUSY_DAYS;

                return isDisabledByBusyDays(d, busyDays, TODAY);
            }

            try {
                return isBusyDay(d, disabledDays);
            } catch (err) {
                console.error(`shouldDisableDate: ${err}`);
            }

            return false;
        },
        [TODAY, disabledDays, field.defaultBusyDays]
    );

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignContent: "center",
                alignItems: "center",
            }}
        >
            <div className={styles.xapp_date_picker_container}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                        className={styles.xapp_date_picker}
                        disableHighlightToday={false}
                        disablePast={true}
                        fixedWeekNumber={6}
                        loading={loading}
                        maxDate={MAX_DATE}
                        onChange={onChange}
                        renderLoading={() => <RenderLoading />}
                        shouldDisableDate={shouldDisableDate}
                        showDaysOutsideCurrentMonth={true}
                        slots={{ day: CustomDay }}
                        value={dateTimeValue}
                        views={["day"]}
                    />
                </LocalizationProvider>
            </div>

            {loading && (
                <FormHelperText
                    error
                    style={{
                        color: "inherit",
                        marginLeft: "98px",
                        marginTop: "14px",
                        marginBottom: "-14px",
                    }}
                >
                    {loadingMessage}
                </FormHelperText>
            )}

            {!loading && errorMessage && (
                <FormHelperText
                    error
                    style={{ marginLeft: "14px", marginTop: "14px", marginBottom: "-14px" }}
                >
                    {errorMessage}
                </FormHelperText>
            )}
        </div>
    );
};

export default FormFieldDate;
